import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Image,
  Avatar,
  VStack,
  Divider,
  Tag,
  Button,
  useToast,
} from "@chakra-ui/react";

import RecruitingCard from "../../components/SuggestCards/RecrutingCard";
import Insta from "../../images/insta_logo.png";
import Facebook from "../../images/facebook_logo.png";
import Youtube from "../../images/youtube_logo.png";
import tiktok from "../../images/tictok_logo.png";
import { adminSkipCpsContentStep, contractedDetail } from "../../api";
import PersonaDetailModal from "../../components/Modals/PersonaModal";
import ContentModal from "../../components/Modals/ContentModal";
import CampaignVisibilityControl from "../../components/CampaignVisibilityControl";

const PLATFORM_ICONS = {
  instagram: Insta,
  facebook: Facebook,
  tiktok: tiktok,
  youtube: Youtube,
};

const creatorName = (item) =>
  [item?.nickname, item?.displayName, item?.username, item?.email]
    .map((value) => (typeof value === "string" ? value.trim() : value))
    .find(Boolean) || "-";

export default function ContractedDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [proposal, setProposal] = useState(null);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [isContentOpen, setIsContentOpen] = useState(false);
  const [personaOpen, setPersonaOpen] = useState(false);
  const [skipLoading, setSkipLoading] = useState(false);

  const loadDetail = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const res = await contractedDetail({ id });

      setProposal(res.data);
      setCreators(res.data.creators ?? []);
      setSelectedCreator(res.data.creators[0]);
    } catch (e) {
      console.error("proposalDetail error:", e);
    } finally {
      setLoading(false);
    }
  };

  // 제안서 상세 조회 API 연동
  useEffect(() => {
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <Flex h="300px" align="center" justify="center">
        로딩 중...
      </Flex>
    );
  }

  if (!proposal) {
    return (
      <Flex h="300px" align="center" justify="center" color="gray.500">
        데이터를 불러올 수 없습니다.
      </Flex>
    );
  }

  const avg =
    creators.length === 0
      ? 0
      : creators.reduce((sum, c) => sum + c.targetValue, 0) / creators.length;

  const isCps =
    String(proposal?.campaign?.billingModel ?? "").toUpperCase() === "CPS";
  const cpsContentStepSkipped = Boolean(proposal?.campaign?.cpsContentStepSkipped);
  const campaignId = proposal?.campaign?.campaignId ?? proposal?.campaign?.id ?? id;
  const canSkipCpsContentStep = isCps && !cpsContentStepSkipped;
  const platformFeeRate = Number(proposal?.proposal?.platformFeeRate ?? 0);
  const totalManuscriptFeeFromProposal = Number(
    proposal?.proposal?.totalManuscriptFee ?? 0
  );
  const totalManuscriptFeeSum = creators.reduce(
    (sum, c) => sum + Number(c.manuscriptFee || 0),
    0
  );
  const totalManuscriptFee =
    totalManuscriptFeeFromProposal > 0
      ? totalManuscriptFeeFromProposal
      : totalManuscriptFeeSum;
  const totalWithFee = Math.floor(totalManuscriptFee * (1 + platformFeeRate));

  const getCommissionRateText = (c) => {
    const proposalCandidates = [
      proposal?.proposal?.unitPrice,
      proposal?.proposal?.unit_price,
      proposal?.proposal?.commissionRate,
      proposal?.proposal?.costPerAction,
    ];
    const creatorCandidates = [
      c?.unitPrice,
      c?.unit_price,
      c?.commissionRate,
      c?.costPerAction,
    ];
    const effectiveRate =
      creatorCandidates
        .map((v) => Number(v))
        .find((v) => Number.isFinite(v) && v > 0) ??
      proposalCandidates
        .map((v) => Number(v))
        .find((v) => Number.isFinite(v) && v > 0);
    if (!Number.isFinite(effectiveRate) || effectiveRate <= 0) return "미정";
    const pct = Math.round(effectiveRate * 10000) / 100;
    return `${String(pct).replace(/\.00$/, "")}%`;
  };

  const handleSkipCpsContentStep = async () => {
    if (!window.confirm("CPS 콘텐츠 제작 단계를 스킵하고 성과측정 단계로 진행할까요?")) {
      return;
    }
    try {
      setSkipLoading(true);
      await adminSkipCpsContentStep({ campaignId });
      toast({
        title: "콘텐츠 단계를 스킵했습니다.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      await loadDetail();
    } catch (e) {
      toast({
        title: "콘텐츠 단계 스킵 실패",
        description:
          e?.response?.data?.error?.message ||
          e?.response?.data?.message ||
          "콘텐츠 단계를 스킵할 수 없습니다.",
        status: "error",
        duration: 2500,
        isClosable: true,
      });
    } finally {
      setSkipLoading(false);
    }
  };

  return (
    <>
      <Box p="40px" bg="white" borderRadius="16px">
        <Flex justifyContent="space-between" mb="16px">
          <Button variant="outline" onClick={() => navigate("/suggest/contracted")}>
            목록으로
          </Button>
          <Flex gap="8px">
            {isCps ? (
              <Button
                colorScheme="purple"
                variant={canSkipCpsContentStep ? "solid" : "outline"}
                isDisabled={!canSkipCpsContentStep}
                isLoading={skipLoading}
                onClick={handleSkipCpsContentStep}
              >
                {cpsContentStepSkipped ? "콘텐츠 단계 스킵됨" : "콘텐츠 단계 스킵"}
              </Button>
            ) : null}
            <CampaignVisibilityControl
              campaignId={campaignId}
              hidden={Boolean(proposal?.campaign?.deletedAt)}
              onChanged={(nextHidden) =>
                setProposal((prev) =>
                  prev
                    ? {
                        ...prev,
                        campaign: {
                          ...prev.campaign,
                          deletedAt: nextHidden ? new Date().toISOString() : null,
                        },
                      }
                    : prev
                )
              }
            />
          </Flex>
        </Flex>
        <RecruitingCard key={creators.id} item={proposal?.campaign} nobutton />
        <Divider border="1px solid #CAC4D0" my="40px" />
        <Flex gap="40px">
          <Flex w="50%" flexDir="column" gap="20px">
            <Flex gap="4px">
              <Text color="#5D6979" fontSize="16px" fontWeight={500}>
                {proposal.proposal.proposalSentAt?.split("T")[0]}
              </Text>
              <Text color="#0054C0" fontSize="16px" fontWeight={600}>
                {proposal.proposal.proposalSentBy}
              </Text>
            </Flex>
            <Text fontSize="24px" fontWeight={700}>
              {proposal.proposal.proposalTitle}
            </Text>
            <Flex
              border="1px solid #D1D4D9"
              borderRadius="8px"
              p="12px"
              h="120px"
              flexDir="column"
            >
              <Text color="#5D6979" fontWeight={500} fontSize="16px">
                제안 설명
              </Text>
              <Box
                maxH="160px"
                overflowY="auto"
                pr="6px" // 스크롤바 겹침 방지
                css={{
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#CBD5E0",
                    borderRadius: "4px",
                  },
                }}
              >
                <Text fontSize="16px" fontWeight={500} whiteSpace="pre-wrap">
                  {proposal.proposal.proposalDescription}
                </Text>
              </Box>
            </Flex>
            {!isCps && (
              <Flex
                border="1px solid #D1D4D9"
                borderRadius="8px"
                p="12px"
                flexDir="column"
              >
                <Text color="#5D6979" fontWeight={500} fontSize="12px">
                  믹스된 크리에이터의 평균 비용
                </Text>
                <Text fontWeight={500} fontSize="16px">
                  {proposal.campaign.billingModel} / {avg.toLocaleString()}KRW
                </Text>
              </Flex>
            )}
          </Flex>
          <Flex
            w="50%"
            border="1px solid #D1D4D9"
            borderRadius="8px"
            p="16px"
            gap="16px"
            flexDir="column"
          >
            <Text color="#0054C0" fontSize="20px" fontWeight={700}>
              {isCps
                ? "원고료 및 커미션 예상"
                : "캠페인 유형별 KPI 및 예상 비용"}
            </Text>
            {isCps && (
              <Text color="#5D6979" fontSize="14px" fontWeight={700}>
                플랫폼 수수료 {platformFeeRate * 100}%
              </Text>
            )}

            <Flex
              w="100%"
              h="100%"
              overflowY="auto"
              flexDir="column"
              gap="16px"
            >
              {proposal.creators?.map((c) => (
                <Flex key={c.creatorProfileId} w="100%" flexDir="column">
                  {!isCps && (
                    <>
                      <Flex
                        w="100%"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Text color="#5D6979" fontSize="16px" fontWeight={500}>
                          {creatorName(c)} {proposal.campaign.billingModel}{" "}
                          {proposal.campaign.billingModel === "CPUC"
                            ? "(고유 클릭당 비용)"
                            : "(고유 노출당 비용)"}
                        </Text>
                        <Text fontSize="20px" fontWeight={700}>
                          ₩ {Number(c.costPerAction).toLocaleString()}
                        </Text>
                      </Flex>

                      <Flex
                        w="100%"
                        alignItems="center"
                        justifyContent="space-between"
                        borderBottom="1px solid #EDF2F7"
                        pb="5px"
                      >
                        <Text color="#5D6979" fontSize="16px" fontWeight={500}>
                          예상 비용
                        </Text>
                        <Text fontSize="20px" fontWeight={700}>
                          ₩ {Number(c.targetValue).toLocaleString()}
                        </Text>
                      </Flex>
                    </>
                  )}
                  {isCps && (
                    <>
                      <Text color="#5D6979" fontSize="16px" fontWeight={500}>
                        {creatorName(c)}
                      </Text>
                      <Flex
                        w="100%"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Text color="#5D6979" fontSize="16px" fontWeight={500}>
                          - 원고료
                        </Text>
                        <Text fontSize="20px" fontWeight={700}>
                          ₩ {Number(c.manuscriptFee || 0).toLocaleString()}
                        </Text>
                      </Flex>
                      <Flex
                        w="100%"
                        alignItems="center"
                        justifyContent="space-between"
                        borderBottom="1px solid #EDF2F7"
                        pb="5px"
                      >
                        <Text color="#5D6979" fontSize="16px" fontWeight={500}>
                          - 커미션율
                        </Text>
                        <Text fontSize="20px" fontWeight={700}>
                          {getCommissionRateText(c)}
                        </Text>
                      </Flex>
                    </>
                  )}
                </Flex>
              ))}
            </Flex>

            <Divider border="1px solid" />

            {!isCps && (
              <Flex justifyContent="space-between" alignItems="center">
                <Text color="#0054C0" fontSize="16px" fontWeight={700}>
                  예상 총 비용
                </Text>
                <Text color="#0054C0" fontSize="20px" fontWeight={700}>
                  ₩
                  {proposal.creators
                    ?.reduce((sum, c) => sum + Number(c.targetValue || 0), 0)
                    .toLocaleString()}
                </Text>
              </Flex>
            )}
            {isCps && (
              <>
                <Flex justifyContent="space-between" alignItems="center">
                  <Text color="#0054C0" fontSize="16px" fontWeight={700}>
                    원고료 합계
                  </Text>
                  <Text color="#0054C0" fontSize="20px" fontWeight={700}>
                    ₩{totalManuscriptFee.toLocaleString()}
                  </Text>
                </Flex>
                <Flex justifyContent="space-between" alignItems="center">
                  <Text color="#0054C0" fontSize="16px" fontWeight={700}>
                    수수료 포함
                  </Text>
                  <Text color="#0054C0" fontSize="20px" fontWeight={700}>
                    ₩{totalWithFee.toLocaleString()}
                  </Text>
                </Flex>
              </>
            )}
          </Flex>
        </Flex>
        <Divider border="1px solid #CAC4D0" my="40px" />
        <Flex gap="20px">
          {/* 크리에이터 목록 */}
          <Box w="30%" borderRadius="12px" boxShadow="sm">
            <Text fontWeight="700" fontSize="18px" mb="12px">
              믹스된 크리에이터 목록 ({creators.length})
            </Text>

            <VStack
              maxH="580px"
              align="stretch"
              spacing="10px"
              overflowY="auto"
            >
              {creators.map((c) => {
                const isActive =
                  selectedCreator?.creatorProfileId === c.creatorProfileId;
                return (
                  <Flex
                    key={c.creatorProfileId}
                    p="12px"
                    borderRadius="12px"
                    border="1px solid"
                    borderColor={isActive ? "#3182CE" : "#E2E8F0"}
                    bg={isActive ? "#EBF8FF" : "white"}
                    cursor="pointer"
                    onClick={() => setSelectedCreator(c)}
                    gap="12px"
                    flexDir="column"
                  >
                    <Flex gap="12px" alignItems="center">
                      <Avatar name={creatorName(c)} w="48px" h="48px" />
                      <Text fontWeight="700">{creatorName(c)}</Text>
                      {PLATFORM_ICONS[c.platform?.toLowerCase()] && (
                        <Image
                          w="24px"
                          h="24px"
                          src={PLATFORM_ICONS[c.platform.toLowerCase()]}
                          alt={c.platform}
                        />
                      )}
                    </Flex>

                    <Flex gap="12px" mt="2px">
                      <StatBox label="팔로워" value={c.followers} />
                      <StatBox label="게시물" value={c.posts} />
                      <StatBox label="평균 조회수" value={c.avgViews} />
                    </Flex>
                  </Flex>
                );
              })}
            </VStack>
          </Box>

          {/* 선택된 크리에이터 상세 정보 */}
          <Box w="70%" bg="white" borderRadius="12px" boxShadow="sm">
            <Text fontWeight="700" fontSize="18px" mb="16px">
              선택된 크리에이터 상세 정보
            </Text>

            {!selectedCreator ? (
              <Flex h="200px" align="center" justify="center" color="gray.500">
                크리에이터를 선택해주세요
              </Flex>
            ) : (
              <Flex flexDir="column" gap="20px">
                <Flex gap="20px">
                  <Field label="이름" value={creatorName(selectedCreator)} />
                  <Field label="이메일" value={selectedCreator.email} />
                </Flex>
                {/* 상세 필드 */}
                <Flex gap="20px">
                  <Field label="팔로워" value={selectedCreator.followers} />
                  <Field label="게시물" value={selectedCreator.posts} />
                  <Field label="평균 조회수" value={selectedCreator.avgViews} />
                </Flex>
                <Flex gap="20px">
                  <Field
                    label="성별"
                    value={
                      selectedCreator?.gender === "male"
                        ? "남성"
                        : selectedCreator?.gender === "female"
                        ? "여성"
                        : "기타"
                    }
                  />
                  <Field label="생년월일" value={selectedCreator.birthDate} />
                  <Field label="국가/언어" value={selectedCreator.language} />
                </Flex>
                <Box>
                  <Text fontWeight="400" fontSize="16x" mb="6px">
                    산업 카테고리
                  </Text>
                  <Flex
                    h="44px"
                    gap="6px"
                    border="1px solid #D1D4D9"
                    p="12px"
                    borderRadius="6px"
                    alignItems="center"
                    overflowX="auto"
                    overflowY="hidden"
                    css={{
                      "&::-webkit-scrollbar": { display: "none" },
                      scrollbarWidth: "none",
                    }}
                  >
                    {selectedCreator?.industryCategories?.map((cat, idx) => (
                      <Tag
                        key={idx}
                        px="8px"
                        py="2px"
                        bg="#E9F2FF"
                        color="#4B5563"
                        fontSize="12px"
                        fontWeight={500}
                        borderRadius="6px"
                        flexShrink={0}
                      >
                        {cat.nameKo}
                      </Tag>
                    ))}
                  </Flex>
                </Box>
                <Box>
                  <Text fontWeight="400" fontSize="16x" mb="6px">
                    라이프스타일 카테고리
                  </Text>
                  <Flex
                    h="44px"
                    gap="6px"
                    border="1px solid #D1D4D9"
                    p="12px"
                    borderRadius="6px"
                    alignItems="center"
                    overflowX="auto"
                    overflowY="hidden"
                    css={{
                      "&::-webkit-scrollbar": { display: "none" },
                      scrollbarWidth: "none",
                    }}
                  >
                    {selectedCreator?.lifestyleCategories?.map((cat, idx) => (
                      <Tag
                        key={idx}
                        px="8px"
                        py="2px"
                        bg="#E9F2FF"
                        color="#4B5563"
                        fontSize="12px"
                        fontWeight={500}
                        borderRadius="6px"
                        flexShrink={0}
                      >
                        {cat.nameKo}
                      </Tag>
                    ))}
                  </Flex>
                </Box>

                <Flex w="100%" gap="10px">
                  <Button
                    w="100%"
                    bgColor="#0054C0"
                    color="white"
                    onClick={() => setIsContentOpen(true)}
                  >
                    콘텐츠 기획안
                  </Button>
                  <Button
                    w="100%"
                    bgColor="#0054C0"
                    color="white"
                    onClick={() => setPersonaOpen(true)}
                  >
                    페르소나
                  </Button>
                </Flex>
              </Flex>
            )}
          </Box>
        </Flex>
      </Box>
      <PersonaDetailModal
        isOpen={personaOpen}
        onClose={() => setPersonaOpen(false)}
        creator={selectedCreator}
        data={proposal?.campaign?.campaignId}
      />
      <ContentModal
        isOpen={isContentOpen}
        onClose={() => setIsContentOpen(false)}
        creator={selectedCreator}
        data={proposal?.campaign?.campaignId}
      />
    </>
  );
}

/** 공용 필드 컴포넌트 */
function Field({ label, value }) {
  return (
    <Flex
      w="100%"
      flexDir="column"
      border="1px solid #E2E8F0"
      borderRadius="8px"
      justifyContent="center"
      alignItems="center"
      p="8px"
    >
      <Text fontSize="14px" color="#5D6979" fontWeight={500}>
        {label}
      </Text>
      <Text fontSize="16px" fontWeight={600}>
        {value}
      </Text>
    </Flex>
  );
}

function StatBox({ label, value }) {
  return (
    <Flex
      w="100%"
      p="8px"
      flexDir="column"
      gap="4px"
      borderRadius="8px"
      border="1px solid #D1D4D9"
      alignItems="center"
    >
      <Text color="#5D6979" fontWeight={500} fontSize="12px">
        {label}
      </Text>
      <Text color="#172940" fontWeight={600} fontSize="16px">
        {value}
      </Text>
    </Flex>
  );
}
